import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";

export type CaptchaMode = "v3" | "v2";
export type CaptchaSolution = { token: string; mode: CaptchaMode };

type Grecaptcha = {
  ready: (callback: () => void) => void;
  render: (container: HTMLElement, params: Record<string, unknown>) => number;
  execute(siteKey: string, options: { action: string }): Promise<string>;
  reset: (widgetId: number) => void;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    __nsRecaptchaOnLoad?: () => void;
  }
}

const V3_ACTION = "login";
const V3_TIMEOUT_MS = 15_000;
// Long enough for a person to tick the checkbox and work through a challenge (token TTL is ~2 min).
const V2_TIMEOUT_MS = 120_000;

/**
 * Google reCAPTCHA for the CDC login CAPTCHA step: v3 first, v2 checkbox as the fallback.
 *
 * - v3 (environment.recaptchaV3SiteKey): score-based, never shows a challenge.
 *   CDC verifies the token server-side and applies its risk/score threshold.
 * - v2 checkbox (environment.recaptchaSiteKey, must be a checkbox-type key): a visible
 *   "I'm not a robot" widget rendered into a container the caller provides. Used when
 *   v3 isn't configured, or after CDC rejects a v3 token.
 *
 * Both share one lazily loaded api.js. Every token is single-use (~2 min TTL) and
 * is returned straight to the caller - nothing is cached. Secret keys live in the
 * CDC Console; this service never contacts Google's siteverify endpoint.
 *
 * When neither site key is set, enabled === false and callers skip the CAPTCHA path.
 */
@Injectable({ providedIn: "root" })
export class RecaptchaService {
  private v2Widget: { id: number; container: HTMLElement } | null = null;
  private scriptReady: Promise<void> | null = null;
  private pending: { resolve: (token: string) => void; reject: (reason: unknown) => void } | null = null;

  public get v3Enabled(): boolean {
    return !!environment.recaptchaV3SiteKey;
  }

  public get v2Enabled(): boolean {
    return !!environment.recaptchaSiteKey;
  }

  public get enabled(): boolean {
    return this.v3Enabled || this.v2Enabled;
  }

  public get firstMode(): CaptchaMode {
    return this.v3Enabled ? "v3" : "v2";
  }

  public get canFallBackToV2(): boolean {
    return this.v3Enabled && this.v2Enabled;
  }

  /** v2 renders its checkbox into `container`, which must be visible while this runs. */
  public async solve(mode: CaptchaMode, container?: HTMLElement): Promise<CaptchaSolution> {
    const token = mode === "v3" ? await this.executeV3() : await this.executeV2(container);
    return { token, mode };
  }

  private async executeV3(): Promise<string> {
    const siteKey = environment.recaptchaV3SiteKey;
    if (!siteKey) {
      throw new Error("recaptcha: no v3 site key configured for this environment");
    }
    await this.loadScript();
    const grecaptcha = window.grecaptcha!;

    return this.withTimeout(
      new Promise<string>((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(siteKey, { action: V3_ACTION }).then(resolve, reject);
        });
      }),
      V3_TIMEOUT_MS,
      "recaptcha v3",
    );
  }

  private async executeV2(container?: HTMLElement): Promise<string> {
    if (!this.v2Enabled) {
      throw new Error("recaptcha: no v2 site key configured for this environment");
    }
    if (!container) {
      throw new Error("recaptcha v2: no container element to render the checkbox into");
    }
    await this.loadScript();

    if (this.pending) {
      this.pending.reject(new Error("recaptcha: superseded by a newer solve() call"));
      this.pending = null;
    }

    return this.withTimeout(
      new Promise<string>((resolve, reject) => {
        this.pending = { resolve, reject };
        try {
          this.renderOrResetV2(container);
        } catch (err) {
          this.pending = null;
          reject(err);
        }
      }),
      V2_TIMEOUT_MS,
      "recaptcha v2",
      () => this.settle(null, new Error("recaptcha v2: timed out")),
    );
  }

  private renderOrResetV2(container: HTMLElement): void {
    const grecaptcha = window.grecaptcha!;
    if (this.v2Widget?.container === container) {
      grecaptcha.reset(this.v2Widget.id);
      return;
    }
    this.v2Widget = {
      container,
      id: grecaptcha.render(container, {
        sitekey: environment.recaptchaSiteKey,
        callback: (token: string) => this.settle(token, null),
        "error-callback": () => this.settle(null, new Error("recaptcha: error-callback")),
        "expired-callback": () => this.settle(null, new Error("recaptcha: token expired")),
      }),
    };
  }

  private loadScript(): Promise<void> {
    if (this.scriptReady) {
      return this.scriptReady;
    }

    this.scriptReady = new Promise<void>((resolve, reject) => {
      if (typeof document === "undefined") {
        reject(new Error("recaptcha: not running in a browser"));
        return;
      }

      if (window.grecaptcha?.render) {
        resolve();
        return;
      }

      window.__nsRecaptchaOnLoad = () => window.grecaptcha!.ready(resolve);

      // render=<v3 key> is what enables grecaptcha.execute(siteKey, ...) for v3; the v2
      // checkbox is still rendered explicitly on top of the same script.
      const renderParam = environment.recaptchaV3SiteKey || "explicit";
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?onload=__nsRecaptchaOnLoad&render=${encodeURIComponent(renderParam)}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error("recaptcha: widget script failed to load"));
      document.head.appendChild(script);
    });

    return this.scriptReady;
  }

  // Google's callbacks never fire if the widget is blocked (e.g. Firefox strict
  // cookie partitioning), so without a timeout the login form would hang forever.
  private withTimeout<T>(promise: Promise<T>, ms: number, label: string, onTimeout?: () => void): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        onTimeout?.();
        reject(new Error(`${label}: timed out after ${ms}ms`));
      }, ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (err) => {
          clearTimeout(timer);
          reject(err);
        },
      );
    });
  }

  private settle(token: string | null, error: unknown | null): void {
    const p = this.pending;
    this.pending = null;
    if (this.v2Widget && window.grecaptcha) {
      try {
        window.grecaptcha.reset(this.v2Widget.id);
      } catch {
        // reset can throw if the widget was never rendered or its container is gone - safe to ignore
      }
    }
    if (!p) {
      return;
    }
    if (error) {
      p.reject(error);
    } else {
      p.resolve(token as string);
    }
  }
}
